import {
	AnswerReveal,
	PointsRow,
	StandardPatternBody,
	Zoomable,
} from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import {
	analyzeVisualQuestion,
	patternAnswerHtml,
	visualAnswerHtml,
} from '@quizzach/utils';
import {
	OptList,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ImageCredits } from '@quizzach/layouts';

interface IVisualQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
}
type VisualOption = 'a' | 'b' | 'c' | 'd';

const getAnswerOptions = (question: IQuestion): VisualOption[] | null => {
	const visualQuestion = question.visual_question;
	if (!visualQuestion?.a || !visualQuestion?.b || !visualQuestion?.c) {
		return null;
	}
	return visualQuestion.d ? ['a', 'b', 'c', 'd'] : ['a', 'b', 'c'];
};

/* Same look as the abcd question type; lives outside the pattern/plain
   switch so options survive a pattern-styled question text. */
const VisualOptions = ({ question }: { question: IQuestion }) => {
	const options = getAnswerOptions(question);
	if (!options) return null;

	return (
		<OptList>
			{options.map((option) => (
				<div className='opt' key={option}>
					<span className='L'>{option}</span>
					<span
						className='t'
						dangerouslySetInnerHTML={{
							__html: question.visual_question?.[option] || '',
						}}
					/>
				</div>
			))}
		</OptList>
	);
};

interface IVisualQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
	hideQuestion?: boolean;
}

/* Answer page shows the alternative (e.g. "revealed") image when uploaded. */
const visualImageFilename = (question: IQuestion, answer = false) =>
	(answer && question.visual_question?.answer_filename) ||
	question.visual_question?.filename ||
	'';

const visualImageUrl = (filename: string) =>
	`${import.meta.env.VITE_ASSETS_URL}/visual_questions/${filename}`;

const useVisualImage = (filename: string) => {
	const [isImgLoading, setIsImgLoading] = useState(true);
	const loadStartTime = useRef<number>(Date.now());
	const timeoutRef = useRef<number>();

	useEffect(() => {
		setIsImgLoading(true);
		loadStartTime.current = Date.now();
	}, [filename]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleImageLoad = () => {
		const elapsed = Date.now() - loadStartTime.current;
		const remainingTime = 200 - elapsed;

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		if (remainingTime > 0) {
			timeoutRef.current = window.setTimeout(
				() => setIsImgLoading(false),
				remainingTime
			);
		} else {
			setIsImgLoading(false);
		}
	};

	return { isImgLoading, handleImageLoad };
};

const VisualImageBlock = ({
	currentQuestion,
	filename,
	isImgLoading,
	handleImageLoad,
}: {
	currentQuestion: IQuestion;
	filename: string;
	isImgLoading: boolean;
	handleImageLoad: () => void;
}) => (
	<div className='img-wrapper'>
		{isImgLoading && <div className='img-loading-skeleton'></div>}
		<motion.figure>
			<Zoomable>
				<motion.img
					src={visualImageUrl(filename)}
					alt='visual-question-img'
					onLoad={handleImageLoad}
					style={{ display: isImgLoading ? 'none' : 'block' }}
					initial={{ opacity: 0 }}
					animate={{ opacity: isImgLoading ? 0 : 1 }}
					transition={{ duration: 0.5 }}
				/>
			</Zoomable>
			{!isImgLoading && currentQuestion.visual_question?.licence_needed && (
				<ImageCredits currentQuestion={currentQuestion} />
			)}
		</motion.figure>
	</div>
);

export const VisualQuestionMobile = (props: IVisualQuestionProps) => {
	const { color, currentQuestion } = props;
	const filename = visualImageFilename(currentQuestion);
	const { isImgLoading, handleImageLoad } = useVisualImage(filename);
	const analysis = useMemo(
		() => analyzeVisualQuestion(currentQuestion),
		[currentQuestion]
	);

	if (analysis.pattern || analysis.note || analysis.kicker) {
		return (
			<>
				<StandardPatternBody analysis={analysis} color={color} />
				<VisualOptions question={currentQuestion} />
				<QuestionContainer color={color}>
					<VisualImageBlock
						currentQuestion={currentQuestion}
						filename={filename}
						isImgLoading={isImgLoading}
						handleImageLoad={handleImageLoad}
					/>
					<PointsRow
						points={currentQuestion.points}
						explanation={currentQuestion.points_explanation}
					/>
				</QuestionContainer>
			</>
		);
	}

	return (
		<QuestionContainer color={color}>
			<QuestionText style={{ marginBottom: '20px' }}>
				<div dangerouslySetInnerHTML={{ __html: analysis.questionHtml }} />
			</QuestionText>
			<VisualOptions question={currentQuestion} />
			<VisualImageBlock
				currentQuestion={currentQuestion}
				filename={filename}
				isImgLoading={isImgLoading}
				handleImageLoad={handleImageLoad}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</QuestionContainer>
	);
};

export const VisualQuestionAnswerMobile = (
	props: IVisualQuestionAnswerProps
) => {
	const {
		questionContainerColor,
		currentQuestionNumber,
		currentQuestion,
		hideQuestion,
	} = props;
	const filename = visualImageFilename(currentQuestion, true);
	const { isImgLoading, handleImageLoad } = useVisualImage(filename);
	const analysis = useMemo(
		() => analyzeVisualQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{!hideQuestion &&
			(analysis.pattern || analysis.note || analysis.kicker) ? (
				<>
					<StandardPatternBody
						analysis={analysis}
						color={questionContainerColor}
						dim
					/>
					<VisualOptions question={currentQuestion} />
					<QuestionContainer color={questionContainerColor}>
						<VisualImageBlock
							currentQuestion={currentQuestion}
							filename={filename}
							isImgLoading={isImgLoading}
							handleImageLoad={handleImageLoad}
						/>
					</QuestionContainer>
				</>
			) : (
				<QuestionContainer color={questionContainerColor}>
					{!hideQuestion && (
						<>
							<QuestionText $small style={{ marginBottom: '20px' }}>
								<div
									dangerouslySetInnerHTML={{ __html: analysis.questionHtml }}
								/>
							</QuestionText>
							<VisualOptions question={currentQuestion} />
							<VisualImageBlock
								currentQuestion={currentQuestion}
								filename={filename}
								isImgLoading={isImgLoading}
								handleImageLoad={handleImageLoad}
							/>
						</>
					)}
				</QuestionContainer>
			)}
			<AnswerReveal
				revealKey={currentQuestionNumber}
				answerHtml={patternAnswerHtml(
					analysis.pattern,
					visualAnswerHtml(
						currentQuestion.visual_question?.answer || '',
						currentQuestion.visual_question
					)
				)}
				explanation={currentQuestion.visual_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
